import { useEffect, useMemo, useState } from 'react';
import isEqual from 'lodash.isequal';
import { ResponseError, responseErrorsResolver } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  onFilterChange,
  onPageChange,
  onSectionChange,
} from './store/slices/permissions';
import { PermissionEnum } from '../user-roles/types';
import { handleConfirmPopup } from './store/slices/confirm';
import ColumnWithConfirmHOC from './column-with-confirm';
import permissionsTree from '../user-roles/variables';
import ColumnWithToggle from '../../components/common/column/column-with-toggle';
import sectionActions from './variables';
import { EditPermissionsRequest, PermissionColumnsTypes } from './types';
import { useEditPermissionsMutation } from './store/services/users';
import getPreparePermissions from './helpers/get-permissions';
import {
  onChangeHelperArray,
  onChangeHelperSingle,
} from './helpers/on-change-helper';

import cn from './styles.module.css';

const PermissionColumns = ({
  isLoading,
  error,
}: PermissionColumnsTypes): React.JSX.Element => {
  const [defaultRoleSectionActions, setDefaultSectionActions] = useState<
    PermissionEnum[] | undefined
  >();

  const dispatch = useAppDispatch();

  const {
    userList: {
      permissions: {
        sectionId,
        pageId,
        search,
        allActionsSectionFiltered,
        userPermissions,
      },
      users: { selectedUser, userId },
      confirm: { popupIsOpen },
    },
  } = useAppSelector((state) => state);

  const { activePermissions, allPermissions, cardsEnabled, defaultRoles } =
    useAppSelector((state) => state.administrationDictionaries);

  const permissionEnumKeys = Object.keys(PermissionEnum).map((item) => item);
  const unassignedPermissions = allPermissions?.filter((item) =>
    permissionEnumKeys.every((permission) => item !== permission),
  );

  let nextPermissionsTree = permissionsTree;

  if (!cardsEnabled) {
    nextPermissionsTree = permissionsTree.filter(
      (item) => item.key !== PermissionEnum.SECTION_CARDS,
    );
  }

  if (unassignedPermissions?.length) {
    const unassignedPermissionsPages = unassignedPermissions.filter(
      (item) => !item.includes('_ACTION'),
    );

    nextPermissionsTree = [
      ...nextPermissionsTree,
      {
        key: 'SECTION_UNASSIGNED' as PermissionEnum,
        pages: [
          {
            key: 'UNASSIGNED_LIST' as PermissionEnum,
            actions: unassignedPermissions
              .filter((item) => item.includes('_ACTION'))
              .map((item) => ({
                key: item,
              })),
          },
          ...unassignedPermissionsPages.map((item) => ({
            key: item,
          })),
        ],
      },
    ];
  }

  const columnsData = getPreparePermissions(nextPermissionsTree);

  const [values, setValues] = useState<PermissionEnum[] | undefined>();

  const [edit, { isLoading: editIsLoading, isSuccess }] =
    useEditPermissionsMutation();
  const [globalError, setGlobalError] = useState('');

  const sectionsItems = userId ? columnsData : undefined;

  const pagesItems =
    userId && sectionId ? columnsData[sectionId]?.pages : undefined;
  const actionsItems =
    userId && sectionId && pageId && pageId !== 'all'
      ? columnsData[sectionId]?.pages?.[pageId]?.actions
      : undefined;

  const activeSections = useMemo(
    () => values?.filter((value) => value.includes('SECTION')),
    [values],
  );
  const activePages = useMemo(
    () =>
      values?.filter(
        (value) => !value.includes('SECTION') && !value.includes('_ACTION'),
      ),
    [values],
  );
  const activeActions = useMemo(
    () => values?.filter((value) => value.includes('_ACTION')),
    [values],
  );

  const onChange = (
    key: PermissionEnum | PermissionEnum[],
    value: boolean,
    columnsDataItem: any,
  ) => {
    if (Array.isArray(key)) {
      onChangeHelperArray(
        key,
        value,
        columnsDataItem,
        columnsData,
        setValues as React.Dispatch<React.SetStateAction<PermissionEnum[]>>,
        activePages,
        pageId,
      );
    } else {
      onChangeHelperSingle(
        key,
        value,
        columnsDataItem,
        columnsData,
        setValues as React.Dispatch<React.SetStateAction<PermissionEnum[]>>,
        activePages,
        activeActions,
      );
    }
  };

  const onSelectRow = (
    key: PermissionEnum | 'all',
    name: 'sectionId' | 'pageId',
  ) => {
    if (name === 'sectionId') {
      dispatch(onSectionChange(key as PermissionEnum));
    } else if (name === 'pageId') {
      dispatch(onPageChange(key));
    }
  };

  useEffect(() => {
    if (userPermissions?.permissions) {
      setValues(userPermissions?.permissions);
    }
  }, [userPermissions?.permissions]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(handleConfirmPopup(false));
    }
  }, [isSuccess]);

  useEffect(() => {
    if (userPermissions && userPermissions.permissions && values) {
      const isDirty =
        userPermissions?.permissions?.filter((item) =>
          values.every((value) => item !== value),
        )?.length > 0 ||
        values?.filter((item) =>
          userPermissions?.permissions?.every(
            (permission) => item !== permission,
          ),
        )?.length;

      if (isDirty) {
        dispatch(handleConfirmPopup(true));
      } else {
        dispatch(handleConfirmPopup(false));
      }
    }
  }, [userPermissions, values]);

  const onSubmit = async () => {
    setGlobalError('');

    const response = (await edit({
      body: {
        permissions: values?.filter(
          (item) =>
            item !== ('SECTION_UNASSIGNED' as PermissionEnum) &&
            item !== ('UNASSIGNED_LIST' as PermissionEnum) &&
            item !== ('CUSTOMERS_SESSIONS_DELETE' as PermissionEnum) &&
            item !== ('EMPLOYEES_SESSIONS_DELETE' as PermissionEnum) &&
            item !== PermissionEnum.OTHER_LIST,
        ),
      } as EditPermissionsRequest,
      id: selectedUser?.id as string,
    })) as { error: ResponseError };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError,
      });
    }
  };

  const onCancel = () => {
    dispatch(handleConfirmPopup(false));
    setValues(userPermissions?.permissions || []);
  };

  const allActionsSection =
    userId && sectionId ? sectionActions[sectionId] : undefined;

  const onSearchPermissions = (value: string) => {
    dispatch(onFilterChange(value));
  };

  const isEditable = activePermissions?.includes(
    PermissionEnum.USERS_EDIT_ACTION,
  );

  useEffect(() => {
    if (defaultRoles && selectedUser) {
      const currentDefaultRole = defaultRoles?.find(
        (role) => selectedUser?.employeeRoleTemplateId === role.id,
      );

      if (currentDefaultRole) {
        const defaultRolePermissions = currentDefaultRole?.permissions;

        if (defaultRolePermissions) {
          const defaultRoleActivePermissions: PermissionEnum[] = [];

          if (pageId === 'all' && allActionsSectionFiltered) {
            Object.keys(allActionsSectionFiltered).forEach((item) => {
              if (
                defaultRolePermissions.find((permission) => item === permission)
              ) {
                defaultRoleActivePermissions.push(item as PermissionEnum);
              }
            });
          } else if (pageId !== 'all' && actionsItems) {
            Object.keys(actionsItems).forEach((item) => {
              if (
                defaultRolePermissions.find((permission) => item === permission)
              ) {
                defaultRoleActivePermissions.push(item as PermissionEnum);
              }
            });
          }

          if (
            !defaultRoleSectionActions ||
            (defaultRoleSectionActions &&
              !isEqual(defaultRoleSectionActions, defaultRoleActivePermissions))
          ) {
            setDefaultSectionActions(defaultRoleActivePermissions);
          }
        }
      }
    }
  }, [
    defaultRoles,
    selectedUser,
    allActionsSectionFiltered,
    pageId,
    actionsItems,
    defaultRoleSectionActions,
  ]);

  return (
    <ColumnWithConfirmHOC
      onSubmit={onSubmit}
      onCancel={onCancel}
      globalError={globalError}
      editIsLoading={editIsLoading}
    >
      <div className={cn.columnWrapper}>
        <ColumnWithToggle
          activePermissions={activeSections as PermissionEnum[]}
          columnData={sectionsItems}
          isLoading={isLoading}
          onChange={onChange}
          onSelectRow={(key: PermissionEnum | 'all') =>
            onSelectRow(key, 'sectionId')
          }
          selectedId={sectionId}
          isEditable={isEditable}
          title="Sections"
          description={`for ${
            selectedUser
              ? `${selectedUser?.profile?.firstName || ''} ${
                  selectedUser?.profile?.lastName || ''
                }`
              : ''
          }`}
          emptyTitle="—"
          hasAllToggleButton={false}
          error={error}
          popupIsOpen={popupIsOpen}
        />
      </div>

      <div className={cn.columnWrapper}>
        <ColumnWithToggle
          activePermissions={activePages as PermissionEnum[]}
          columnData={pagesItems}
          isLoading={isLoading}
          onChange={onChange}
          isEditable={isEditable}
          onSelectRow={(key: PermissionEnum | 'all') =>
            onSelectRow(key, 'pageId')
          }
          selectedId={pageId}
          title="Pages"
          emptyTitle="—"
          allActionsSectionButton={Boolean(allActionsSection)}
          error={error}
          popupIsOpen={popupIsOpen}
        />
      </div>

      <div className={cn.columnWrapper}>
        <ColumnWithToggle
          activePermissions={activeActions as PermissionEnum[]}
          columnData={
            pageId !== 'all' ? actionsItems : allActionsSectionFiltered
          }
          isLoading={isLoading}
          onChange={onChange}
          isEditable={isEditable}
          title="Action permissions"
          emptyTitle="View only"
          selectedId={pageId}
          sectionId={sectionId}
          onSearch={onSearchPermissions}
          search={search}
          error={error}
          popupIsOpen={popupIsOpen}
          defaultRoleSectionActions={defaultRoleSectionActions}
          values={values}
          setValues={setValues}
          userPermissions={userPermissions?.permissions}
          actionColumn
        />
      </div>
    </ColumnWithConfirmHOC>
  );
};

export default PermissionColumns;
